import type { SearchableSelectOption } from "../components/ui/searchableSelect.tsx";
import type { TicketIssueType, TicketIssueCategory } from "../api/ticket.ts";

const FALLBACK_GROUP = "Övrigt";

/**
 * Builds grouped SearchableSelect options for the issue-type picker (Kyla,
 * IQ-box, Nätverk, …). Shared by the two "create ticket/problem" forms so
 * grouping/ordering never drifts between them.
 *
 * Uncategorized issue types (CategoryID null, or pointing at a category not
 * present in `categories` — e.g. the seed migration hasn't run on this
 * database yet) fall back to a single "Övrigt" group rather than disappearing,
 * so the picker degrades to today's flat list when no categories exist.
 */
export function buildIssueTypeOptions(
  issueTypes: TicketIssueType[],
  categories: TicketIssueCategory[],
): SearchableSelectOption[] {
  const categoryById = new Map(categories.filter((c) => !c.deleted).map((c) => [c.ID, c]));

  const categoryFor = (issue: TicketIssueType) =>
    issue.CategoryID != null ? categoryById.get(issue.CategoryID) : undefined;

  return issueTypes
    .filter((i) => !i.deleted)
    .slice()
    .sort((a, b) => {
      const orderA = categoryFor(a)?.SortOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = categoryFor(b)?.SortOrder ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.Issue.localeCompare(b.Issue);
    })
    .map((i) => ({
      value: String(i.ID),
      label: i.Issue,
      group: categoryFor(i)?.Name ?? FALLBACK_GROUP,
    }));
}
